import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';
import type { Pilot2SimulationResult } from '../../types';

// ─── Pilot #2 simulation (compare pilot #1 broken scores vs pilot #2 projected) ──
function Pilot2Simulation() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Pilot2SimulationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await adminApi.runPilot2Simulation();
      setResult(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const toGG = (s: number | null) => (s === null ? '—' : toGGScore(s));
  const deltaCell = (d: number | null) => {
    if (d === null) return <span className="text-slate-400">—</span>;
    const sign = d > 0 ? '+' : '';
    const cls = d > 0
      ? 'text-emerald-700'
      : d < 0 ? 'text-red-700' : 'text-slate-500';
    const gg = Math.round(d * 6);
    return <span className={`font-semibold ${cls}`}>{sign}{gg}</span>;
  };

  const downloadJson = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `pilot2-simulation-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyJson = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      alert('Full simulation JSON copied to clipboard.');
    } catch (e) {
      alert('Clipboard copy failed — use the Download button instead.');
    }
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
      <h2 className="text-sm font-semibold text-slate-500 uppercase mb-2">
        Pilot #2 Simulation — "what would pilot #2 engine have scored?"
      </h2>
      <p className="text-xs text-slate-500 mb-4">
        Runs the new pilot #2 engine against the pilot #1 vendors'
        existing DAR data inside an isolated sandbox DB.  Zero prod
        writes.  Returns a week-by-week score comparison.
      </p>
      <button
        onClick={run}
        disabled={busy}
        className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Running simulation… (~1-2 min)' : 'Simulate Pilot #2 on Pilot #1 Data'}
      </button>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 space-y-6">
          <div className="flex gap-2">
            <button
              onClick={downloadJson}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-white hover:bg-slate-900"
            >
              ⬇ Download full JSON
            </button>
            <button
              onClick={copyJson}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-200 text-slate-900 hover:bg-slate-300"
            >
              📋 Copy full JSON
            </button>
          </div>

          {result.caveats && result.caveats.length > 0 && (
            <details className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <summary className="text-xs font-semibold text-amber-900 cursor-pointer">
                ⚠ Read before interpreting ({result.caveats.length} notes)
              </summary>
              <ul className="mt-2 text-xs text-amber-900 space-y-1 list-disc pl-5">
                {result.caveats.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </details>
          )}

          {result.vendors.map((v) => (
            <div key={v.id}>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">
                {v.name}
                {v.active_window && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    active {v.active_window[0]} → {v.active_window[1]}
                    {' '}({v.weeks.length} weeks)
                  </span>
                )}
              </h3>
              {v.note && (
                <p className="text-xs text-slate-500 italic">{v.note}</p>
              )}
              {v.weeks.length > 0 && (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Week</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Pilot #1 (actual)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Pilot #2 (projected)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Δ GG points</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {v.weeks.map((w) => (
                        <WeekRow key={w.week_start} week={w} toGG={toGG} deltaCell={deltaCell} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeekRow({
  week, toGG, deltaCell,
}: {
  week: import('../../types').Pilot2SimulationWeek;
  toGG: (s: number | null) => number | string;
  deltaCell: (d: number | null) => React.ReactElement;
}) {
  const [open, setOpen] = useState(false);
  const d = week.detail;
  const vs = d?.vendor_snapshot;
  const rules = d?.cycle_rule_fires || {};
  const ruleRows = Object.entries(rules)
    .sort(([, a], [, b]) => b.count - a.count);

  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-3 py-2 text-slate-700">{week.week_start}</td>
        <td className={`px-3 py-2 text-right ${
          week.pilot1_score !== null
            ? getScoreColorClass(getScoreColor(week.pilot1_score))
            : 'text-slate-400 italic'
        }`}>
          {week.pilot1_snapshot_exists ? toGG(week.pilot1_score) : <span className="italic">no snapshot</span>}
        </td>
        <td className={`px-3 py-2 text-right ${
          week.pilot2_projected !== null
            ? getScoreColorClass(getScoreColor(week.pilot2_projected))
            : 'text-slate-400'
        }`}>
          {toGG(week.pilot2_projected)}
        </td>
        <td className="px-3 py-2 text-right">{deltaCell(week.delta)}</td>
        <td className="px-3 py-2">
          <button
            onClick={() => setOpen(!open)}
            className="text-xs text-brand-600 hover:underline"
          >
            {open ? 'Hide' : 'Show rules'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50">
          <td colSpan={5} className="px-4 py-3">
            {!d ? (
              <p className="text-xs text-slate-400">(no detail available for this week)</p>
            ) : (
              <div className="space-y-3 text-xs">
                {vs && (
                  <div>
                    <strong className="text-slate-700">Vendor delta breakdown:</strong>{' '}
                    previous {vs.previous_score?.toFixed(2)} → {vs.score.toFixed(2)}{' '}
                    (raw {vs.raw_delta.toFixed(2)}, smoothed {vs.smoothed_delta.toFixed(2)},
                    final <strong>{vs.final_delta >= 0 ? '+' : ''}{vs.final_delta.toFixed(2)}</strong>;
                    positives {vs.positive_total.toFixed(2)}, negatives {vs.negative_total.toFixed(2)})
                  </div>
                )}
                {vs && vs.triggers.length > 0 && (
                  <div>
                    <strong className="text-slate-700">Vendor-level rules fired:</strong>
                    <ul className="list-disc pl-5 mt-1 space-y-0.5">
                      {vs.triggers.map((t, i) => (
                        <li key={i}>
                          <span className={t.is_positive ? 'text-emerald-700' : 'text-red-700'}>
                            {t.score_impact >= 0 ? '+' : ''}{t.score_impact.toFixed(2)}
                          </span>
                          {' '}<strong>{t.rule_name}</strong>
                          {t.explanation && <span className="text-slate-500"> — {t.explanation}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {d.guard_aggregate && d.guard_aggregate.count > 0 && (
                  <div>
                    <strong className="text-slate-700">Guards scored:</strong>{' '}
                    {d.guard_aggregate.count} guards —
                    avg {d.guard_aggregate.avg_score?.toFixed(2)},
                    range {d.guard_aggregate.min_score?.toFixed(2)}..{d.guard_aggregate.max_score?.toFixed(2)}
                  </div>
                )}
                {d.site_aggregate && d.site_aggregate.count > 0 && (
                  <div>
                    <strong className="text-slate-700">Sites scored:</strong>{' '}
                    {d.site_aggregate.count} sites —
                    avg {d.site_aggregate.avg_score?.toFixed(2)},
                    range {d.site_aggregate.min_score?.toFixed(2)}..{d.site_aggregate.max_score?.toFixed(2)}
                  </div>
                )}
                {ruleRows.length > 0 && (
                  <div>
                    <strong className="text-slate-700">All rules fired this cycle (across all entities):</strong>
                    <table className="mt-1 w-full text-xs">
                      <thead>
                        <tr className="text-slate-500">
                          <th className="text-left py-1">Rule</th>
                          <th className="text-left py-1">Category</th>
                          <th className="text-left py-1">Family</th>
                          <th className="text-right py-1">Fires</th>
                          <th className="text-right py-1">Total impact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ruleRows.map(([name, info]) => (
                          <tr key={name} className="border-t border-slate-200">
                            <td className="py-1">
                              <span className={info.is_positive ? 'text-emerald-700' : 'text-red-700'}>●</span>{' '}
                              {name}
                            </td>
                            <td className="py-1 text-slate-500">{info.category || '—'}</td>
                            <td className="py-1 text-slate-500">{info.severity_family || '—'}</td>
                            <td className="py-1 text-right">{info.count}</td>
                            <td className={`py-1 text-right font-semibold ${
                              info.total_impact > 0 ? 'text-emerald-700'
                              : info.total_impact < 0 ? 'text-red-700' : 'text-slate-500'
                            }`}>
                              {info.total_impact >= 0 ? '+' : ''}{info.total_impact.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Pilot #2 QA actions (buttons below the KPIs) ────────────────────
type ActionResult = { label: string; output: string; isError: boolean };

function Pilot2Actions() {
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<ActionResult[]>([]);

  const run = async (
    label: string,
    fn: () => Promise<any>,
    confirmMsg?: string,
  ) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(label);
    try {
      const data = await fn();
      const output = typeof data === 'string'
        ? data
        : JSON.stringify(data, null, 2);
      setResults((prev) => [{ label, output, isError: false }, ...prev].slice(0, 10));
    } catch (e: any) {
      const msg = e?.response?.data
        ? JSON.stringify(e.response.data, null, 2)
        : String(e?.message || e);
      setResults((prev) => [{ label, output: msg, isError: true }, ...prev].slice(0, 10));
    } finally {
      setBusy(null);
    }
  };

  const btn = (label: string, variant: 'safe' | 'danger', onClick: () => void) => (
    <button
      key={label}
      onClick={onClick}
      disabled={!!busy}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        variant === 'danger'
          ? 'bg-red-600 text-white hover:bg-red-700'
          : 'bg-brand-600 text-white hover:bg-brand-700'
      }`}
    >
      {busy === label ? 'Running…' : label}
    </button>
  );

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
      <h2 className="text-sm font-semibold text-slate-500 uppercase mb-2">
        Pilot #2 QA Actions
      </h2>
      <p className="text-xs text-slate-500 mb-4">
        Buttons run pilot #2 management commands against the live DB.
        Safe (blue) commands execute immediately and show results below.
        Writing (red) commands require confirmation.  Slow commands run
        in the background — refresh this page in ~1-2 min to see new
        data in the sections below.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {btn('Compute success metrics', 'safe', () =>
          run('Compute success metrics', adminApi.runSuccessMetrics))}
        {btn('Process queued uploads', 'safe', () =>
          run('Process queued uploads', adminApi.runProcessQueuedUploads))}
        {btn('Rematch orphans (DRY-RUN)', 'safe', () =>
          run('Rematch orphans (DRY-RUN)', () => adminApi.runRematch(false)))}
        {btn('Rematch orphans (LIVE)', 'danger', () =>
          run('Rematch orphans (LIVE)', () => adminApi.runRematch(true),
              'This writes to the DB.  It flips orphan parsed events to matched guards and bumps UnresolvedGuardName.resolved.  Continue?'))}
        {btn('Aggregate 14 days', 'danger', () =>
          run('Aggregate 14 days', () => adminApi.runAggregate(14),
              'This writes DailyShiftSummary rows for the past 14 days.  Continue?'))}
        {btn('Trigger scoring cycle', 'danger', () =>
          run('Trigger scoring cycle', adminApi.runScoringCycle,
              'This triggers run_weekly_scoring.  Honors SCORING_ENABLED kill switch.  Continue?'))}
      </div>

      {results.length > 0 && (
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase">
            Recent Runs (newest first — cleared on page refresh)
          </h3>
          {results.map((r, i) => (
            <details key={i} className="rounded-lg border border-slate-200 bg-slate-50">
              <summary className={`cursor-pointer px-3 py-2 text-sm font-medium ${
                r.isError ? 'text-red-700' : 'text-slate-900'
              }`}>
                {r.isError ? '✗ ' : '✓ '}{r.label}
              </summary>
              <pre className="px-3 py-2 text-xs text-slate-700 bg-slate-900 text-slate-100 overflow-auto max-h-96 whitespace-pre-wrap">
{r.output}
              </pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function TerminateVendorAtSite() {
  const [vendorId, setVendorId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const submit = async () => {
    if (!vendorId || !siteId) return;
    if (!window.confirm(
      `This closes all of this vendor's guard assignments at this site.\n`
      + `Future weeks will score as frozen_no_obligation for this vendor at this site.\n\n`
      + `Vendor: ${vendorId}\nSite: ${siteId}\n\nContinue?`
    )) return;
    setBusy(true);
    setResult(null);
    setIsError(false);
    try {
      const data = await adminApi.terminateVendorAtSite(vendorId, siteId);
      setResult(JSON.stringify(data, null, 2));
    } catch (e: any) {
      setIsError(true);
      setResult(e?.response?.data
        ? JSON.stringify(e.response.data, null, 2)
        : String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
      <h2 className="text-sm font-semibold text-slate-500 uppercase mb-2">
        Terminate Vendor at Site
      </h2>
      <p className="text-xs text-slate-500 mb-4">
        Use when a client has ended a vendor's contract at a specific site.
        Bulk-closes all of that vendor's guard assignments at the site —
        the next scoring cycle treats the site as{' '}
        <code className="rounded bg-slate-100 px-1">frozen_no_obligation</code>{' '}
        for this vendor (score holds, no negative impact).
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          placeholder="Vendor UUID"
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          disabled={busy}
        />
        <input
          type="text"
          placeholder="Site UUID"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          disabled={busy}
        />
        <button
          onClick={submit}
          disabled={busy || !vendorId || !siteId}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Closing…' : 'Terminate'}
        </button>
      </div>
      {result && (
        <pre className={`px-3 py-2 text-xs rounded-lg overflow-auto max-h-48 whitespace-pre-wrap ${
          isError
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-slate-900 text-slate-100'
        }`}>
{result}
        </pre>
      )}
    </div>
  );
}

type Period = '7d' | '30d' | '90d' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'all': 'All time',
};

function KpiCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4">
      <p className="text-xs font-medium text-slate-400 uppercase">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function statusVariant(s: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  switch (s) {
    case 'completed': return 'success';
    case 'processing':
    case 'queued': return 'warning';
    case 'failed': return 'error';
    case 'partial': return 'info';
    default: return 'neutral';
  }
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diffMs / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function AdminPilotSummary() {
  const [period, setPeriod] = useState<Period>('30d');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'pilot-summary', period],
    queryFn: () => adminApi.getPilotSummary(period),
  });

  return (
    <>
      <PageHeader
        title="Pilot Summary"
        description="Platform activity aggregated for stakeholder review"
        actions={
          <div className="flex rounded-xl border-2 border-slate-200 overflow-hidden">
            {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  period === p
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        }
      />

      {isLoading && (
        <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">Failed to load pilot summary.</p>
        </div>
      )}

      {data && (
        <>
          {/* Participation KPIs (lifetime — who is on the platform) */}
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">Participation</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <KpiCard label="Vendors" value={data.vendors_total} />
            <KpiCard label="Clients" value={data.clients_total} />
            <KpiCard label="Active Sites" value={data.sites_total} />
            <KpiCard label="Active Guards" value={data.guards_total} />
          </div>

          <Pilot2Simulation />
          <Pilot2Actions />
          <TerminateVendorAtSite />

          {/* Ingestion Health */}
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">
            Ingestion Health <span className="normal-case text-slate-400 font-normal">— {PERIOD_LABELS[period]}</span>
          </h2>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
              <KpiCard
                label="Total Uploads"
                value={data.uploads.total}
                sub={data.uploads.avg_processing_ms
                  ? `avg ${(data.uploads.avg_processing_ms / 1000).toFixed(1)}s each`
                  : undefined}
              />
              <KpiCard
                label="Completed"
                value={data.uploads.completed}
                sub={`${data.uploads.completed_pct}%`}
              />
              <KpiCard label="Failed" value={data.uploads.by_status.failed ?? 0} />
              <KpiCard
                label="In Flight"
                value={(data.uploads.by_status.queued ?? 0) + (data.uploads.by_status.processing ?? 0)}
              />
              <KpiCard label="Partial" value={data.uploads.by_status.partial ?? 0} />
            </div>
            {data.uploads.total === 0 && (
              <p className="text-sm text-slate-400">No uploads in this period.</p>
            )}
          </div>

          {/* Data Quality */}
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">
            Data Quality <span className="normal-case text-slate-400 font-normal">— {PERIOD_LABELS[period]}</span>
          </h2>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
              <KpiCard label="Events Parsed" value={data.events.total} />
              <KpiCard
                label="Guard Match Rate"
                value={`${data.events.match_rate_pct}%`}
                sub={`${data.events.matched} of ${data.events.total} matched`}
              />
              <KpiCard label="Unmatched" value={data.events.unmatched} />
              <KpiCard label="Incidents Flagged" value={data.events.incidents} />
              <KpiCard label="With Photos" value={data.events.with_photos} />
            </div>

            {data.events.top_unmatched_names.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">
                  Top Unmatched Guard Names
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  Names extracted from DARs that don't match a guard on the vendor's roster.
                  Usually means the vendor hasn't added that guard yet.
                </p>
                <div className="flex flex-wrap gap-2">
                  {data.events.top_unmatched_names.map((n, i) => (
                    <div
                      key={`${n.guard_name_raw}-${i}`}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5"
                    >
                      <span className="text-sm text-slate-900">{n.guard_name_raw}</span>
                      <span className="text-xs text-slate-500 font-medium">×{n.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Scoring Engine */}
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">
            Scoring Engine <span className="normal-case text-slate-400 font-normal">— {PERIOD_LABELS[period]}</span>
          </h2>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <KpiCard label="Weekly Cycles Run" value={data.scoring.cycles_run} />
              <KpiCard label="Score Snapshots" value={data.scoring.total_snapshots} />
              <KpiCard label="Rule Triggers" value={data.scoring.total_triggers} />
            </div>

            {data.scoring.latest_cycle ? (
              <div className="border-t border-slate-100 pt-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Most Recent Cycle</h3>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <dt className="text-xs font-medium text-slate-400 uppercase">Week</dt>
                    <dd className="mt-1 text-sm text-slate-900">
                      {data.scoring.latest_cycle.week_start}
                      {data.scoring.latest_cycle.week_end && ` — ${data.scoring.latest_cycle.week_end}`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
                    <dd className="mt-1">
                      <StatusBadge
                        label={data.scoring.latest_cycle.status}
                        variant={data.scoring.latest_cycle.status === 'completed' ? 'success'
                          : data.scoring.latest_cycle.status === 'running' ? 'warning'
                            : 'error'}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-400 uppercase">Snapshots</dt>
                    <dd className="mt-1 text-sm text-slate-900">{data.scoring.latest_cycle.snapshots_created}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-400 uppercase">Triggers</dt>
                    <dd className="mt-1 text-sm text-slate-900">{data.scoring.latest_cycle.triggers_in_cycle}</dd>
                  </div>
                </dl>
              </div>
            ) : (
              <p className="text-sm text-slate-400">No scoring cycles have run in this period.</p>
            )}
          </div>

          {/* Per-Vendor Breakdown */}
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">
            Per-Vendor Breakdown <span className="normal-case text-slate-400 font-normal">— {PERIOD_LABELS[period]}</span>
          </h2>
          <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Uploads</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Completed</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Failed</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Events</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Guards</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">GG Score</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Last Upload</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {data.vendors.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">
                        No vendors on the platform yet.
                      </td>
                    </tr>
                  ) : data.vendors.map((v) => {
                    const ggScore = toGGScore(v.current_score);
                    return (
                      <tr key={v.id}>
                        <td className="px-4 py-3 font-medium text-slate-900">{v.name}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{v.uploads}</td>
                        <td className="px-4 py-3 text-right text-emerald-700">{v.completed_uploads}</td>
                        <td className="px-4 py-3 text-right text-red-700">{v.failed_uploads || '—'}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{v.events}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{v.active_guards}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${getScoreColorClass(getScoreColor(v.current_score))}`}>
                          {ggScore}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500 text-xs">
                          {relativeTime(v.last_upload_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Rules Triggered */}
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">
            Top Rules Triggered <span className="normal-case text-slate-400 font-normal">— {PERIOD_LABELS[period]}</span>
          </h2>
          <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Rule</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Group</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Triggers</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Net Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {data.top_rules.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                        No rules have triggered in this period.
                      </td>
                    </tr>
                  ) : data.top_rules.map((r) => (
                    <tr key={`${r.name}::${r.group}::${r.is_positive}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                      <td className="px-4 py-3 text-slate-500">{r.group}</td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={r.is_positive ? 'positive' : 'negative'}
                          variant={r.is_positive ? 'success' : 'error'}
                        />
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{r.trigger_count}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${
                        r.total_impact > 0 ? 'text-emerald-700' : r.total_impact < 0 ? 'text-red-700' : 'text-slate-500'
                      }`}>
                        {r.total_impact > 0 ? '+' : ''}{r.total_impact.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
